import { useEffect } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { business } from "../data/business";
import { reviews } from "../data/reviews";
import { services } from "../data/services";
const meta = {
  title: {
    en: "Flexhire Solutions LLC | Professional Cleaning & Staffing Services Columbus, Ohio",
    es: "Flexhire Solutions LLC | Servicios Profesionales de Limpieza y Personal en Columbus, Ohio"
  },
  description: {
    en: "Professional cleaning and staffing solutions in Columbus, Ohio. Hotel housekeeping, office cleaning, and workforce support. Reliable, trained staff. Bilingual service. Free estimate.",
    es: "Soluciones profesionales de limpieza y personal en Columbus, Ohio. Limpieza de hoteles, oficinas y apoyo laboral. Personal confiable y capacitado. Servicio bilingüe. Estimado gratis."
  },
  keywords: {
    en: "cleaning services Columbus Ohio, staffing solutions, professional cleaning, hotel housekeeping, office cleaning, professional staff, workforce support Columbus, reliable professional staff",
    es: "servicios de limpieza Columbus Ohio, agencia de personal, limpieza profesional, limpieza de hoteles, limpieza de oficinas, personal bilingüe, apoyo laboral Columbus"
  }
};
export function SeoManager() {
  const {
    lang,
    t
  } = useLanguage();
  useEffect(() => {
    const base = business.websiteUrl;
    const url = `${base}/${lang}/`;
    document.title = t(meta.title);
    upsertMeta("name", "description", t(meta.description));
    upsertMeta("name", "keywords", t(meta.keywords));
    upsertMeta("name", "robots", "index, follow");
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:title", t(meta.title));
    upsertMeta("property", "og:description", t(meta.description));
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", business.heroImage);
    upsertMeta("property", "og:locale", lang === "es" ? "es_US" : "en_US");
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertLink("canonical", url);
    upsertLink("alternate", `${base}/en/`, "en");
    upsertLink("alternate", `${base}/es/`, "es");
    upsertLink("alternate", `${base}/`, "x-default");
    const businessId = `${base}/#business`;
    const graph = [{
      "@type": "Organization",
      "@id": `${base}/#organization`,
      name: business.name,
      url: base,
      logo: business.heroImage,
      email: business.email,
      telephone: business.phoneDisplay,
      slogan: "The Right People. Every Time. Everywhere.",
      contactPoint: {
        "@type": "ContactPoint",
        telephone: business.phoneDisplay,
        contactType: "customer service",
        availableLanguage: ["English", "Spanish"],
        areaServed: "US-OH"
      }
    }, {
      "@type": ["LocalBusiness", "ProfessionalService"],
      "@id": businessId,
      name: business.name,
      description: t(meta.description),
      url: url,
      image: business.heroImage,
      telephone: business.phoneDisplay,
      email: business.email,
      priceRange: "$$",
      address: {
        "@type": "PostalAddress",
        streetAddress: `${business.street} (${business.plaza})`,
        addressLocality: business.city,
        addressRegion: business.region,
        postalCode: business.postalCode,
        addressCountry: "US"
      },
      areaServed: [{
        "@type": "City",
        name: "Columbus, Ohio"
      }, {
        "@type": "AdministrativeArea",
        name: "Franklin County, Ohio"
      }],
      openingHoursSpecification: {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        opens: "07:00",
        closes: "17:00"
      },
      knowsLanguage: ["en", "es"],
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: business.rating,
        reviewCount: business.reviewCount,
        bestRating: "5"
      },
      review: {
        "@type": "Review",
        author: {
          "@type": "Person",
          name: reviews[0].name
        },
        reviewBody: reviews[0].quote.en,
        reviewRating: {
          "@type": "Rating",
          ratingValue: "5",
          bestRating: "5"
        }
      },
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: lang === "es" ? "Servicios" : "Services",
        itemListElement: services.map(s => ({
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: t(s.name),
            description: t(s.description),
            provider: {
              "@id": businessId
            },
            areaServed: "Columbus, Ohio"
          }
        }))
      }
    }, {
      "@type": "BreadcrumbList",
      itemListElement: [{
        "@type": "ListItem",
        position: 1,
        name: lang === "es" ? "Inicio" : "Home",
        item: url
      }, {
        "@type": "ListItem",
        position: 2,
        name: lang === "es" ? "Servicios" : "Services",
        item: `${url}#services`
      }, {
        "@type": "ListItem",
        position: 3,
        name: lang === "es" ? "Empleos" : "Careers",
        item: `${url}#careers`
      }, {
        "@type": "ListItem",
        position: 4,
        name: lang === "es" ? "Contacto" : "Contact",
        item: `${url}#contact`
      }]
    }];
    let script = document.getElementById("ld-json") as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement("script");
      script.id = "ld-json";
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": graph
    });
  }, [lang, t]);
  return null;
}
function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
function upsertLink(rel: string, href: string, hreflang?: string) {
  const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    if (hreflang) el.hreflang = hreflang;
    document.head.appendChild(el);
  }
  el.href = href;
}